<template>
  <div v-bind:style="{
    'padding-bottom': !$vuetify.breakpoint.mdAndUp  ? '56px' : 'initial',
  }">

    <list-navigator
      v-bind:style="{
         zIndex: $vuetify.breakpoint.mdAndUp ? 'initial': '10',
      }"
      v-if="detailView"
      :backButtonHref="listViewQuery"
      :disabled="detail.loadingDetails"
      :total="list.list.length"
      :current="currentItemIndex"
      @navigate="(idx) => viewItemDetails(idx)"
    />

    <router-view v-on:request="request" v-bind="viewData" ref="view" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import { ListNavigator } from '@/components';
import { formatTicketType } from '@/plugins/api-client/ticket/model';
// import { NAMED_ROLES } from '@/store/modules/session';

const getClient = (type) => Vue.api.getClient(type);

export default {
  name: 'TicketsView',
  props: {
    ticketType: {
      type: String,
    },
    isTIC: Boolean,
  },
  components: { ListNavigator },
  beforeRouteUpdate(to, from, next) {
    if (this.currentView === 'list') {
      this.listViewQuery = from.fullPath;
      this.$nextTick(() => {
        this.detail.backURL = from.fullPath;
      });
    } else if (to.path.startsWith('/approvals') || from.path.startsWith('/approvals')) {
      this.listViewQuery = null;
    }
    next();
  },
  data() {
    return {
      listViewPath: null,
      listViewQuery: null,
      list: {
        total: null,
        count: null,
        list: [],
        start: null,
        loading: true,
        firstLoadDone: false,
      },
      detail: {
        details: null,
        loadingDetails: false,
        activities: [],
        suscriptores: [],
        loadingActivities: false,
        backButton: false,
        workFlow: [],
        loadingWorkFlow: false,
        loadingSuscripciones: false,
        isValid: true,
      },
      currentView: null,
    };
  },
  async created() {
    this.suscriptorsClient = this.$api.getClient('suscriptors');
    await this.$store.dispatch('tickets/listApprovals', {});
  },
  methods: {
    request({ type, args }) {
      switch (type) {
      case 'list':
        this.currentView = 'list';
        this.searchList(args);
        break;
      case 'detail':
        this.currentView = type;
        this.searchDetail(args);
        break;
      case 'updateTicketData':
        this.updateTicketData(args);
        break;
      case 'approvals':
        this.currentView = 'list';
        this.searchApprovers(args);
        break;
      case 'pending':
        this.currentView = 'list';
        this.searchPending(args);
        break;
      default: console.warn(`some TicketView children requested unmapped data (${type})`);
      }
      // update base path for list views
      if (this.currentView === 'list') {
        this.listViewPath = this.listURL;
        if (this.listURL == null && this.listView) {
          this.listViewPath = this.$router.currentRoute.path;
        }
      }
    },
    async search(searchFn, action, ticketType, requestType = '') {
      if (action === 'list' && this.$router.currentRoute.fullPath === this.listViewQuery) {
        return;
      }
      const listData = this.list;

      listData.loading = true;
      if (action === 'list') {
        listData.firstLoadDone = false;
      }
      const result = await searchFn();
      if (action === 'list') {
        listData.list = result.list;
        listData.total = result.total;
      } else {
        listData.list = listData.list.concat(result.list);
      }

      if (requestType === 'approvals') {
        this.$store.dispatch('tickets/updateResourceList', {
          resourceList: this.buildResourceList(listData.list),
        });
      }
      listData.loading = false;
      listData.firstLoadDone = true;
      if (ticketType != null) {
        listData.ticketType = ticketType;
      }
    },
    searchList({
      start, size, filters, sort, ticketType, asc, action,
    }) {
      const type = formatTicketType(ticketType);
      const client = getClient(type);
      const searchFn = client.list.bind(client,
        start, size, filters, sort, ticketType, asc);
      this.search(searchFn, action, ticketType);
    },

    async searchApprovers({
      start, size, filters, sort, ticketType, asc, action, status,
    }) {
      const type = formatTicketType(ticketType);
      const client = getClient(type);
      filters.status = status; // eslint-disable-line

      const searchFn = client.listApprovals.bind(client,
        start, size, filters, sort, ticketType, asc);
      this.search(searchFn, action, ticketType, 'approvals');
    },

    async searchPending({
      start, size, filters, action,
    }) {
      const client = getClient('ticket/request');
      const searchFn = client.listPend.bind(client,
        start, size, filters);
      this.search(searchFn, action);
    },

    async searchDetail({ ticketType, idTicket, user }) {
      this.detail.loadingDetails = true;
      try {
        const response = await getClient('ticket/request').detail(
          ticketType, idTicket, user,
        );
        if ((response || {}).status === 404) { // No tienes permisos
          this.$store.dispatch('messages/createNotificationMessage', {
            type: 'error',
            title: this.$t('ticketActions.noView'), // No tienes permisos para visualizar esta solicitud<
            timeout: this.$getConst('notifications.timeoutPlus'),
            dismissible: true,
          });
          this.$router.push('/');
        } else {
          this.detail.details = response.unique();
          this.$refs.view.initToggleMessageVisibility();
          const { referenceNumber } = this.detail.details;
          this.searchActivities(idTicket);
          this.searchWorkFlow(idTicket);
          await this.searchSuscriptor(referenceNumber);
        }
      } catch (ex) {
        // TODO map error in details view
        console.error('error loading ticket details info', ex);
      }
      this.detail.loadingDetails = false;
      this.detail.backButton = this.list.list.length > 0;
    },

    async searchActivities(idTicket) {
      this.detail.loadingActivities = true;
      try {
        // const response = await getClient('activities').list(0, null, { idTicket },
        // 'time_stamp desc');
        const req = getClient('activities').list(0, null, { idTicket }, 'time_stamp desc');
        const response = await req;
        this.detail.activities = req.format(response).list;
      } catch (ex) {
        // TODO map error in details view
        console.error('error loading ticket activities info', ex);
      }
      this.detail.loadingActivities = false;
    },

    async searchWorkFlow(idTicket) {
      this.detail.loadingWorkFlow = true;
      // if (this.hasRoles(NAMED_ROLES.VIEW_CSU) || this.hasRoles(NAMED_ROLES.VIEW_KEYUSER)) {
      try {
        const response = await getClient('zapprlWorkFlow').list(idTicket);
        const list = response.list.sort((workA, workB) => workA.order - workB.order);
        this.detail.workFlow = list;
      } catch (ex) {
        // TODO map error in details view
        console.error('error loading ticket workflow info', ex);
      }
      // }
      this.detail.loadingWorkFlow = false;
    },

    async searchSuscriptor(referenceNumber) {
      this.detail.loadingSuscripciones = true;
      const uuidAplicacionOrigen = this.$getConst('uuidAplicacion.origen');

      try {
        const { id, userid } = this.$store.getters['session/user'];
        const response = await this.suscriptorsClient.getListSuscriptorsSolicitud({
          apiCges: {
            delegado: userid,
            uuidSuscriptor: id,
            endpoint: `cges/solicitudes/${ referenceNumber }/suscriptores?uuidAplicacionOrigen=${ uuidAplicacionOrigen }`,
          },
          id: referenceNumber,
        });

        let arrSus = [];
        if (response?.collection_suscriptors?.suscriptors.length > 0) {
          arrSus = response.collection_suscriptors.suscriptors.map((item) => ({
            name: item.nombre,
            uuid: item.uuid,
            codigoFlujo: item.flujo.id,
          }));
        }

        this.detail.suscriptores = arrSus;
        this.suscriptores = arrSus;
      } catch (error) {
        console.log(error);
      }

      this.detail.loadingSuscripciones = false;
    },

    async updateTicketData(updateFn) {
      if (typeof updateFn === 'function') {
        const { details, activities } = updateFn({
          details: this.detail.details,
          activities: this.detail.activities,
        });

        if (activities != null) {
          this.detail.activities = activities;
        }

        if (details != null) {
          this.detail.details = details;
        }
      }
    },
    async viewItemDetails(idx) {
      const item = this.list.list[idx];
      if (item != null) {
        const type = 'detail';
        const args = {
          ticketType: item.type.relAttr,
          idTicket: item.id,
          user: this.user.id,
        };
        await this.request({ type, args });
        this.$router.push(`${this.listViewPath}/${item.id}`);
      }
    },
    buildResourceList(list = []) {
      return list.reduce((acc, item) => {
        const resource = item?.affectedResource;

        if (!resource) {
          return acc;
        }

        const exists = acc.some((r) => r.value === resource.id);

        if (!exists) {
          acc.push({
            value: resource.id,
            label: resource.name,
          });
        }
        return acc;
      }, []);
    },
  },
  computed: {
    ...mapGetters({
      hasRoles: 'session/hasRole',
    }),
    ...mapState({
      user: ({ session: { session } }) => (session != null ? session.user : null),
    }),
    viewData() {
      let data = {};
      if (this.listView) {
        data = this.list;
      } else if (this.detailView) {
        data = this.detail;
      }
      return data;
    },
    listView() {
      return this.currentView === 'list';
    },
    detailView() {
      return this.currentView === 'detail' && !this.detail.loadingDetails;
    },
    currentItemIndex() {
      const currentItem = this.detail.details;
      return this.list.list.findIndex((item) => item.id === currentItem.id);
    },
  },
};
</script>
